import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import type { Ordine } from '../api/ordini.js';
import { Modale } from '../componenti/Modale.js';
import { giornoItaliano } from '../giorni.js';
import { t } from '../i18n/lingua.js';

/**
 * Far avanzare un ordine.
 *
 * L'interfaccia non conosce la macchina a stati: la chiede al servizio, che
 * risponde con dove si può andare e che cosa serve per andarci. Così cambiare
 * una regola nel file della macchina cambia anche i pulsanti e i campi, senza
 * toccare una riga di qui.
 *
 * Nel v2 questa è una domanda secca — «Salto allo stato 7 o continuo allo
 * stato 4?» — e poi una maschera diversa per ogni passo. Qui le strade si
 * vedono insieme, con scritto che cosa chiedono, così si sceglie sapendo.
 *
 * Tre cose arrivate con le regole del v2: il **vettore** si sceglie da un
 * elenco nel passaggio allo stato 3; i **PDF** che un passaggio pretende si
 * dicono prima, con dove caricarli; e quando il servizio fa una **domanda**
 * («la fattura SPA non c'è, vai avanti lo stesso?») si risponde qui.
 */

export interface Campo {
  key: string;
  label: string;
}

export interface Transizione {
  toStateId: number;
  toSortOrder: number;
  label: string;
  jump: boolean;
  required: Campo[];
  optional: Campo[];
  /** I PDF che devono essere già sull'ordine. */
  documents?: (Campo & { present: boolean })[];
  /** Dopo il passaggio l'ordine parte da solo verso C&L e il vettore. */
  sendsToCarrier?: boolean;
  /** I vettori fra cui scegliere, quando il passaggio ne chiede uno. */
  carriers?: { id: number; label: string }[];
}

/** I campi che si scrivono come testo; il vettore si sceglie; il resto sono date. */
const TESTO = new Set([
  'shipCarrier',
  'containerShipmentNumber',
  'invoiceNumberSpa',
  'invoiceNumberInc',
]);

export function ModaleAvanzamento({
  ordine,
  onChiudi,
  onInvio,
}: {
  ordine: Ordine;
  onChiudi: () => void;
  /** Com'è andato l'invio automatico al vettore e a Business Central, quando c'è stato. */
  onInvio?: (messaggio: string) => void;
}) {
  const client = useQueryClient();
  const [scelta, setScelta] = useState<Transizione | null>(null);
  const [valori, setValori] = useState<Record<string, string>>({});
  /** La domanda del servizio, in attesa di un sì. */
  const [domanda, setDomanda] = useState<string | null>(null);

  const passi = useQuery({
    queryKey: ['transizioni', ordine.id],
    queryFn: () =>
      chiama<{ transitions: Transizione[] }>(`/api/orders/${ordine.id}/transitions`),
  });

  const avanza = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      chiama<Ordine>(`/api/orders/${ordine.id}/advance`, { metodo: 'POST', corpo }),
    onSuccess: (aggiornato) => {
      client.setQueryData(['ordine', ordine.id], aggiornato);
      void client.invalidateQueries({ queryKey: ['ordini'] });
      void client.invalidateQueries({ queryKey: ['ordini-conteggi'] });
      void client.invalidateQueries({ queryKey: ['transizioni', ordine.id] });
      // il vettore e Business Central: si dice com'è andata, una riga sola
      const esiti = [aggiornato.carrierSend?.message, aggiornato.bcSend?.message].filter(Boolean);
      if (aggiornato.bcSend) void client.invalidateQueries({ queryKey: ['invii-bc', ordine.id] });
      if (esiti.length > 0) onInvio?.(esiti.join(' '));
      onChiudi();
    },
    onError: (errore) => {
      if (errore instanceof ErroreApi && errore.codice === 'serveConferma') {
        setDomanda(errore.message);
      }
    },
  });

  const transizioni = passi.data?.transitions ?? [];
  const errore =
    avanza.error instanceof ErroreApi && avanza.error.codice !== 'serveConferma'
      ? avanza.error.message
      : null;
  const mancanti =
    avanza.error instanceof ErroreApi
      ? ((avanza.error.dettagli as { missing?: Campo[] } | undefined)?.missing ?? [])
      : [];

  /** Il valore da mostrare: quello scritto adesso, o quello già sull'ordine. */
  const valore = (chiave: string) => {
    if (valori[chiave] !== undefined) return valori[chiave];
    const gia = (ordine as unknown as Record<string, unknown>)[chiave];
    if (gia === null || gia === undefined) return '';
    return TESTO.has(chiave) || chiave === 'carrierId' ? String(gia) : giornoItaliano(String(gia));
  };

  const mancaFile = (scelta?.documents ?? []).filter((d) => !d.present);

  const conferma = (si = false) => {
    if (!scelta) return;
    const corpo: Record<string, unknown> = { toStateId: scelta.toStateId };
    for (const c of [...scelta.required, ...scelta.optional]) {
      const v = valori[c.key];
      if (v === undefined || v === '') continue;
      corpo[c.key] = c.key === 'carrierId' ? Number(v) : v;
    }
    // il vettore già sull'ordine vale: non serve risceglierlo
    if (
      scelta.required.some((c) => c.key === 'carrierId') &&
      corpo.carrierId === undefined &&
      ordine.carrierId
    ) {
      corpo.carrierId = ordine.carrierId;
    }
    if (si) corpo.confirm = true;
    setDomanda(null);
    avanza.mutate(corpo);
  };

  return (
    <Modale
      titolo={t('Avanza di stato')}
      occhiello={ordine.state}
      larga
      inCorso={avanza.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={avanza.isPending}>
            {t('Annulla')}
          </button>
          {domanda ? (
            <button className="btn btn-primary" onClick={() => conferma(true)} disabled={avanza.isPending}>
              {t('Sì, vai avanti')}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => conferma()}
              disabled={!scelta || avanza.isPending || mancaFile.length > 0}
            >
              {avanza.isPending ? t('Avanzo…') : scelta ? scelta.label : t('Scegli dove andare')}
            </button>
          )}
        </>
      }
    >
      {passi.isPending && <p className="dim">{t('Un momento…')}</p>}

      {!passi.isPending && transizioni.length === 0 && (
        <p className="dim" style={{ margin: 0 }}>
          {t('Questo ordine è arrivato in fondo: non c’è nessuno stato dopo.')}
        </p>
      )}

      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>
            {errore}
            {mancanti.length > 0 && ` ${t('Manca:')} ${mancanti.map((c) => c.label).join(', ')}.`}
          </span>
        </div>
      )}

      {domanda && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">?</span>
          <span>{domanda}</span>
        </div>
      )}

      {transizioni.length > 0 && (
        <div className="strade">
          {transizioni.map((passaggio) => (
            <button
              key={passaggio.toStateId}
              className={scelta?.toStateId === passaggio.toStateId ? 'strada strada-scelta' : 'strada'}
              onClick={() => {
                setScelta(passaggio);
                setValori({});
                setDomanda(null);
              }}
            >
              <span className="strada-nome">
                {passaggio.label}
                {/* il salto si dice: chi lo sceglie deve sapere che sta
                    scavalcando degli stati, non che sta andando avanti */}
                {passaggio.jump && <span className="badge badge-warn">{t('salto')}</span>}
              </span>
              <span className="dim" style={{ fontSize: 11 }}>
                {passaggio.required.length === 0
                  ? t('non chiede niente')
                  : `${t('chiede:')} ${passaggio.required.map((c) => c.label.toLowerCase()).join(', ')}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {scelta && mancaFile.length > 0 && (
        <div className="avviso" role="alert" style={{ marginTop: 14 }}>
          <span className="avviso-segno">▲</span>
          <span>
            {t('Per questo passaggio serve il')} {mancaFile.map((d) => d.label).join(` ${t('e il')} `)}.{' '}
            {t('Caricalo dalla scheda «Fatture e documenti», poi torna qui.')}
          </span>
        </div>
      )}

      {scelta?.sendsToCarrier && (
        <p className="dim" style={{ fontSize: 12, marginTop: 12 }}>
          {t('Confermando, l’ordine parte da solo: i dati a C&L per il DAU e, se il vettore è un altro, al vettore. Solo per i clienti che hanno acceso l’invio ai vettori.')}
        </p>
      )}

      {scelta && (scelta.required.length > 0 || scelta.optional.length > 0) && (
        <div className="fields" style={{ marginTop: 14 }}>
          {[...scelta.required, ...scelta.optional].map((c) => {
            /**
             * Il vettore ha risposto `DEFAULT`: non indica la nave. Il campo
             * si mostra vuoto, ma il passaggio lo conta come risposta — prima
             * la finestra lo dava per obbligatorio e mancante, e poi lo lasciava
             * passare senza spiegare perché.
             */
            const dalVettore =
              valori[c.key] === undefined &&
              ((c.key === 'shipCarrier' && ordine.shipCarrierNotGiven) ||
                (c.key === 'containerShipmentNumber' && ordine.containerNotGiven));
            const obbligatorio = scelta.required.some((r) => r.key === c.key) && !dalVettore;
            const cambia = (v: string) => setValori((x) => ({ ...x, [c.key]: v }));
            return (
              <div className="field" key={c.key}>
                <label htmlFor={`av-${c.key}`}>
                  {c.label}
                  {obbligatorio && <span className="req">*</span>}
                </label>
                {c.key === 'carrierId' ? (
                  <select
                    id={`av-${c.key}`}
                    className="select"
                    value={valore(c.key)}
                    onChange={(e) => cambia(e.target.value)}
                  >
                    <option value="">{t('Scegli…')}</option>
                    {(scelta.carriers ?? []).map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`av-${c.key}`}
                    className="input"
                    type={TESTO.has(c.key) ? 'text' : 'date'}
                    value={valore(c.key)}
                    placeholder={dalVettore ? 'DEFAULT' : undefined}
                    onChange={(e) => cambia(e.target.value)}
                  />
                )}
                {dalVettore && (
                  <div className="hint">
                    {c.key === 'shipCarrier'
                      ? t('{vettore} non indica la nave (DEFAULT): va bene così, o scrivila se la sai.', {
                          vettore: ordine.carrier ?? t('Il vettore'),
                        })
                      : t('{vettore} non indica il container (DEFAULT): va bene così, o scrivilo se lo sai.', {
                          vettore: ordine.carrier ?? t('Il vettore'),
                        })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modale>
  );
}
